import { Form } from "@commonlibs";
import clsx from "clsx";
import { useEffect, useState } from "react";
import FormGrid from "../FormGrid/FormGrid";
import { FormRow } from "../FormGrid/FormGrid.types";
import MainScaffold from "../MainScaffold/MainScaffold";
import { FormScaffoldFormGridWrapper } from "./FormScaffold.styles";
import { BaseFormScaffoldState, FormScaffoldProps } from "./FormScaffold.types";
import FormScaffoldHeader from "./FormScaffoldHeader/FormScaffoldHeader";
import FormScaffoldResetEvent from "./utils/FormScaffoldResetEvent";
import { hasChangedData } from "./utils/hasDataChanged";

function FormScaffold<T extends BaseFormScaffoldState>({
    className,
    state = {} as T,
    onSubmit,
    rows,
    isLoading = false,
    style,
    footer,
    link,
    linkText,
    isSuccess = false,
    reset,
    ...rest
}: FormScaffoldProps<T>) {
    const [editing, setEditing] = useState(false);

    function handleSubmit(newData: T) {
        if (hasChangedData(newData, state)) onSubmit(newData);
        else setEditing(false);
    }

    function notifyReset() {
        FormScaffoldResetEvent.dispatch(state);
    }

    function handleOnCancel() {
        notifyReset();
        setEditing(false);
    }

    function handleOnEdit() {
        setEditing(true);
    }

    useEffect(() => {
        if (editing && isSuccess) {
            reset?.();
            setEditing(false);
        }
    }, [editing, isLoading]);

    useEffect(() => {
        notifyReset();
    }, [state]);

    return (
        <Form onSubmit={handleSubmit} className={clsx("form-scaffold", className)} style={style}>
            <MainScaffold {...rest}>
                {{
                    header: (
                        <FormScaffoldHeader
                            onEdit={handleOnEdit}
                            onCancel={handleOnCancel}
                            isLoading={isLoading}
                            isEditing={editing}
                            link={link}
                            linkText={linkText}
                        />
                    ),
                    content: (
                        <FormScaffoldFormGridWrapper>
                            {Array.isArray(rows[0]) ? (
                                rows.map((row, index) => {
                                    return <FormGrid key={index} editable={editing} rows={row as FormRow[]} />;
                                })
                            ) : (
                                <FormGrid editable={editing} rows={rows as FormRow[]} />
                            )}
                        </FormScaffoldFormGridWrapper>
                    ),
                    footer: typeof footer === "function" ? footer({ isEditing: editing, isLoading }) : footer,
                }}
            </MainScaffold>
        </Form>
    );
}

export default FormScaffold;
